/**
 * Tag filter row (Track A · P0.6). A set of toggle chips — one per known
 * tag — that drives an AND filter over a record list (selecting more tags
 * narrows results; see `matchesTags`). Purely presentational: the host owns
 * the selected-set state and applies the filter. Renders nothing when there
 * are no tags to filter by, so it can be dropped unconditionally above any
 * list.
 */

import type { CSSProperties } from "react";
import { resolveTagAccentKey, resolveTagChipStyle } from "@/lib/tags";

type TagFilterProps = {
  /** All tags available to filter by (e.g. from `collectTags`). */
  allTags: readonly string[];
  /** Currently active tags. */
  selected: readonly string[];
  onChange: (next: string[]) => void;
  /** Map a stored tag to its display label (e.g. strip a namespace). */
  formatLabel?: (tag: string) => string;
  /**
   * Ambient workshop context for free-form tags. Literal `WS1`…`WS8`
   * chips always use that workshop's own workstream accent.
   */
  workshopId?: string;
  className?: string;
};

const CHIP_BASE: CSSProperties = {
  padding: "2px 10px",
  borderRadius: 999,
  fontSize: "var(--fs-label)",
  lineHeight: 1.5,
  cursor: "pointer",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.28)",
  background: "transparent",
  color: "var(--text-muted)",
};

export function TagFilter({
  allTags,
  selected,
  onChange,
  formatLabel,
  workshopId,
  className,
}: TagFilterProps) {
  if (allTags.length === 0) return null;
  const selectedSet = new Set(selected);

  function toggle(tag: string): void {
    const next = selectedSet.has(tag)
      ? selected.filter((t) => t !== tag)
      : [...selected, tag];
    onChange(next);
  }

  return (
    <div
      className={className}
      role="group"
      aria-label="Filter by tag"
      style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
    >
      {allTags.map((tag) => {
        const on = selectedSet.has(tag);
        const accentKey = resolveTagAccentKey(tag, workshopId);
        const wsStyle = resolveTagChipStyle(tag, workshopId, { selected: on });
        return (
          <button
            key={tag}
            type="button"
            onClick={() => toggle(tag)}
            aria-pressed={on}
            data-tag-accent={accentKey}
            className="tag-chip"
            style={{ ...CHIP_BASE, ...wsStyle }}
          >
            {formatLabel ? formatLabel(tag) : tag}
          </button>
        );
      })}
      {selected.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([])}
          className="modal-close-btn"
          style={{ flex: "none" }}
        >
          Clear tags
        </button>
      )}
    </div>
  );
}
